import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Wifi, Loader2, Lock, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/password-reset/${token}`)
      .then(({ data }) => { setEmail(data.email); setState("ready"); })
      .catch((e) => {
        setErrorMsg(formatApiErrorDetail(e.response?.data?.detail) || "Reset link invalid");
        setState("error");
      });
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset", { token, new_password: pw });
      setState("done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="nhp-dark min-h-screen bg-nhp-bg text-nhp-text grid place-items-center">
      <div className="w-full max-w-md p-8 border border-nhp-border bg-nhp-surface" data-testid="reset-password-page">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 grid place-items-center bg-nhp-primary"><Wifi className="h-4 w-4 text-nhp-on-primary" strokeWidth={2.5} /></div>
          <div>
            <div className="font-heading font-black">NetOnLain</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Admin password reset</div>
          </div>
        </div>

        {state === "loading" && <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

        {state === "error" && (
          <div className="py-12 text-center" data-testid="reset-error">
            <XCircle className="h-10 w-10 mx-auto text-nhp-critical" />
            <h2 className="mt-4 font-heading font-black text-2xl">{errorMsg}</h2>
            <p className="mt-2 text-sm text-nhp-muted">Ask an admin to issue a new reset link.</p>
            <Link to="/admin/login" className="mt-6 inline-block text-nhp-primary font-mono text-xs uppercase tracking-[0.2em]">← Back to sign in</Link>
          </div>
        )}

        {state === "ready" && (
          <div className="mt-8 space-y-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary">// SET NEW PASSWORD</div>
            <div className="text-sm text-nhp-muted">For <span className="font-mono text-nhp-text">{email}</span></div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">New password</Label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nhp-muted" />
                <Input data-testid="reset-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                       className="pl-10 h-11 bg-nhp-bg border-nhp-border rounded-none" />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Confirm password</Label>
              <Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                     className="mt-1 h-11 bg-nhp-bg border-nhp-border rounded-none" />
            </div>
            <Button data-testid="reset-submit-btn" onClick={submit} disabled={busy}
                    className="w-full h-11 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPDATE PASSWORD"}
            </Button>
          </div>
        )}

        {state === "done" && (
          <div className="py-12 text-center" data-testid="reset-done">
            <CheckCircle2 className="h-10 w-10 mx-auto text-nhp-primary" />
            <h2 className="mt-4 font-heading font-black text-2xl">Password updated</h2>
            <p className="mt-2 text-sm text-nhp-muted">You can now sign in with your new password.</p>
            <Button onClick={() => nav("/admin/login")} data-testid="reset-goto-login-btn"
                    className="mt-6 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
              GO TO SIGN IN
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
